/**
 * Partner → Setup → Payments (TMMPDM-0082): a tenant's billing client config through tmm-api
 * (`GET /v1/admin/tenants/{slug}/billing/client-config`, the cached pay-api public keys) and the
 * admin action **Refresh billing config** (`POST …/refresh`, TMMAPI-0129: audited, safe to repeat).
 * The config is owned by pay-api; nothing here edits it.
 *
 * The panel shows the fields as sent and composes no verdict of its own: an absent key shows "—",
 * and the publishable key is masked to its last six characters. An empty cache (503
 * `PAY_API_UNAVAILABLE`) is an info alert. tmm-api answers a refresh with the same 503 code both
 * when pay-api reports the tenant unconfigured (the cache is cleared) and when it can't be reached
 * (the cache is unchanged), with nothing to tell them apart, so one sentence covers both and the
 * config is read again. The server's `message` is never shown (TMM-B3).
 *
 * The Refresh button is never disabled: a click while a refresh is running is ignored, the busy
 * state is `aria-busy` plus a polite status line, and focus stays on the button throughout. A failed
 * refresh's sentence (TMMPDM-0083, A11Y-010) goes into that same status line once, and the next click
 * clears it; a success is announced by the toast. The alerts are static notes, not live regions, so
 * nothing is announced twice.
 *
 * TMMPDM-0084 (tmm-api 0130): a "Billing connection" row reads the tenant's `billingProvisionedAt`
 * alone ("Provisioned {{date}}" or "Not provisioned"), and **Provision billing…** (always enabled: the
 * action is idempotent) opens a confirm. A success replaces the row and the config (a `null`
 * `clientConfig` is the empty-cache note), toasts, and hands the stamped tenant to the page. A failure
 * stays inside the open confirm, worded from `details.reason` alone; `ACTIVATION_REFUSED` offers Copy
 * details for support (pay-api's code is copied, never shown).
 */
import { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useTranslation } from 'react-i18next';
import { useCopy } from '@/hooks/useCopy';
import { ConfirmDialog, ErrorState, SectionCard, useNotify } from '@/components/page';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { adminApi } from '@/api/tmm/admin';
import { ApiError, errorCopy } from '@/api/errors';
import type { ScopedBillingClientConfig, TenantView } from '@/api/tmm/types';
import { NOT_SENT, formatInstant } from '@/format';
import {
  provisionFailureReason,
  provisionPayApiCode,
  provisionReasonWords,
  provisionWordedReason,
} from './provisionBilling';

interface PaymentsSetupPanelProps {
  tenant: TenantView;
  /** The tenant record after a provisioning, so the rest of the page sees the stamp. */
  onUpdated: (tenant: TenantView) => void;
}

type View =
  | { status: 'loading' }
  | { status: 'ready'; config: ScopedBillingClientConfig }
  | { status: 'empty' }
  | { status: 'error'; error: unknown };

type Notice = { severity: 'warning' | 'error'; message: string };

const MASKED_KEY_CHARS = 6;

/** 503 `PAY_API_UNAVAILABLE`: an empty cache on the GET, or pay-api's own answer on a refresh. */
function isPayApiUnavailable(error: unknown): boolean {
  return error instanceof ApiError && String(error.code) === 'PAY_API_UNAVAILABLE';
}

function shown(value: string | undefined): string {
  return value === undefined || value === '' ? NOT_SENT : value;
}

/** The publishable key is public, but the console still shows only its tail: "…" + 6 characters. */
function maskedKey(key: string | undefined): string {
  return key === undefined || key === '' ? NOT_SENT : `…${key.slice(-MASKED_KEY_CHARS)}`;
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography component="dt" variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography component="dd" variant="body2" sx={{ m: 0, overflowWrap: 'anywhere' }}>
        {value}
      </Typography>
    </Box>
  );
}

export function PaymentsSetupPanel({ tenant, onUpdated }: PaymentsSetupPanelProps) {
  const slug = tenant.slug;
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const notify = useNotify();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [provisioning, setProvisioning] = useState(false);
  const [provisionFailure, setProvisionFailure] = useState<{ error: unknown } | null>(null);
  const provisionErrorRef = useRef<HTMLDivElement>(null);
  // A failed provisioning moves the focus into the confirm's error, once per failure.
  useEffect(() => {
    if (provisionFailure) provisionErrorRef.current?.focus();
  }, [provisionFailure]);
  const [view, setView] = useState<View>({ status: 'loading' });
  const [notice, setNotice] = useState<Notice | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const refreshingRef = useRef(false);
  // A later answer (a refresh, or the next slug) supersedes one still in flight.
  const epoch = useRef(0);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const mine = ++epoch.current;
    adminApi
      .getBillingClientConfig(slug)
      .then((config) => {
        if (!cancelled && epoch.current === mine) setView({ status: 'ready', config });
      })
      .catch((error: unknown) => {
        if (!cancelled && epoch.current === mine) {
          setView(isPayApiUnavailable(error) ? { status: 'empty' } : { status: 'error', error });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [slug, reloadToken]);

  const reread = async () => {
    const mine = ++epoch.current;
    try {
      const config = await adminApi.getBillingClientConfig(slug);
      if (epoch.current === mine) setView({ status: 'ready', config });
    } catch (error) {
      if (epoch.current === mine) {
        setView(isPayApiUnavailable(error) ? { status: 'empty' } : { status: 'error', error });
      }
    }
  };

  const refresh = async () => {
    if (refreshingRef.current) return;
    refreshingRef.current = true;
    setRefreshing(true);
    setNotice(null);
    try {
      const config = await adminApi.refreshBillingClientConfig(slug);
      // Only an applied refresh supersedes a read still in flight; a failed one leaves it to land.
      epoch.current += 1;
      setView({ status: 'ready', config });
      notify.success(t('paymentsSetup.refreshed', 'Billing setup refreshed.'));
    } catch (error) {
      if (isPayApiUnavailable(error)) {
        setNotice({
          severity: 'warning',
          message: t(
            'paymentsSetup.refreshUnavailable',
            "pay-api has no billing setup for this tenant, or couldn't be reached. Check the tenant in pay-api, then refresh again.",
          ),
        });
        await reread();
      } else {
        setNotice({
          severity: 'error',
          message: errorCopy(
            error,
            t,
            t('paymentsSetup.refreshError', 'Could not refresh the billing setup.'),
          ),
        });
      }
    } finally {
      refreshingRef.current = false;
      setRefreshing(false);
    }
  };

  const provision = async () => {
    if (provisioning) return;
    setProvisioning(true);
    setProvisionFailure(null);
    try {
      const state = await adminApi.provisionTenantBilling(slug);
      // The answer's config supersedes any read still in flight; `null` is the empty-cache note.
      epoch.current += 1;
      setView(
        state.clientConfig === null
          ? { status: 'empty' }
          : { status: 'ready', config: state.clientConfig },
      );
      onUpdated({ ...tenant, billingProvisionedAt: state.billingProvisionedAt });
      setConfirmOpen(false);
      notify.success(t('paymentsSetup.provision.done', 'Billing provisioned.'));
    } catch (error) {
      setProvisionFailure({ error });
    } finally {
      setProvisioning(false);
    }
  };

  const copyProvisionDetails = async (error: unknown) => {
    const lines = [
      `payApiCode: ${provisionPayApiCode(error) ?? '-'}`,
      `slug: ${slug}`,
      `reason: ${provisionFailureReason(error) ?? '-'}`,
    ];
    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      notify.success(t('components.copied', 'Copied'));
    } catch {
      notify.info(t('components.copyFailed', "Couldn't copy — select the text instead."));
    }
  };

  /** The failure's words, inside the confirm (TMM-B3 / TMM-B4): keyed on `details.reason` alone. */
  const provisionError = (error: unknown) => {
    const reason = provisionWordedReason(error);
    return (
      // The busy confirm button is disabled, which drops the focus: it lands here (the error, which the
      // alert announces), still inside the confirm, so a retry or Cancel is a keystroke away (TMM-B4).
      <Box ref={provisionErrorRef} tabIndex={-1} sx={{ outline: 'none' }}>
        {reason === null
          ? errorCopy(error, t, t('paymentsSetup.provision.error', 'Could not provision billing.'))
          : provisionReasonWords(reason, t)}
        {reason === 'ACTIVATION_REFUSED' ? (
          <Box sx={{ mt: 1 }}>
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              onClick={() => void copyProvisionDetails(error)}
            >
              {t('components.copyDetails', 'Copy details for support')}
            </Button>
          </Box>
        ) : null}
      </Box>
    );
  };

  const config = view.status === 'ready' ? view.config : null;
  const providerFields = Object.entries(config?.providerFields ?? {});

  return (
    <Box>
      <Box
        role="status"
        sx={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          m: '-1px',
          overflow: 'hidden',
          clip: 'rect(0 0 0 0)',
          whiteSpace: 'nowrap',
        }}
      >
        {refreshing
          ? t('paymentsSetup.refreshing', 'Refreshing billing config…')
          : (notice?.message ?? '')}
      </Box>

      {notice ? (
        <Alert severity={notice.severity} role="note" sx={{ mb: 2 }}>
          {notice.message}
        </Alert>
      ) : null}
      {view.status === 'empty' ? (
        <Alert severity="info" role="note" sx={{ mb: 2 }}>
          {t(
            'paymentsSetup.empty',
            'No billing setup is cached for this tenant. Refresh to read it from pay-api.',
          )}
        </Alert>
      ) : null}

      <SectionCard
        title={t('paymentsSetup.title', 'Billing setup')}
        actions={
          <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
            <Button
              variant="outlined"
              aria-busy={refreshing ? 'true' : undefined}
              startIcon={
                refreshing ? (
                  <CircularProgress size={16} color="inherit" aria-hidden />
                ) : (
                  <RefreshIcon />
                )
              }
              onClick={() => void refresh()}
            >
              {t('paymentsSetup.refresh', 'Refresh billing config')}
            </Button>
            <Button variant="outlined" onClick={() => setConfirmOpen(true)}>
              {t('paymentsSetup.provision.open', 'Provision billing…')}
            </Button>
          </Stack>
        }
      >
        <Box component="dl" sx={{ m: 0, maxWidth: 720 }}>
          <Field
            label={t('paymentsSetup.connection.label', 'Billing connection')}
            value={
              tenant.billingProvisionedAt === null
                ? t('paymentsSetup.connection.notProvisioned', 'Not provisioned')
                : t('paymentsSetup.connection.provisioned', 'Provisioned {{date}}', {
                    date: formatInstant(tenant.billingProvisionedAt, i18n.language),
                  })
            }
          />
        </Box>

        {view.status === 'loading' ? <LoadingSpinner /> : null}
        {view.status === 'error' ? (
          <ErrorState
            error={view.error}
            message={t('paymentsSetup.loadError', 'Could not load the billing setup.')}
            onRetry={() => {
              setView({ status: 'loading' });
              setReloadToken((n) => n + 1);
            }}
          />
        ) : null}

        {config ? (
          <Box
            component="dl"
            sx={{
              m: 0,
              display: 'grid',
              gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' },
              gap: 2,
              maxWidth: 720,
            }}
          >
            <Field
              label={t('paymentsSetup.field.provider', 'Provider')}
              value={shown(config.provider)}
            />
            <Field
              label={t('paymentsSetup.field.environment', 'Environment')}
              value={shown(config.environment)}
            />
            <Field label={t('paymentsSetup.field.flow', 'Flow')} value={shown(config.flow)} />
            <Field
              label={t('paymentsSetup.field.stripeAccount', 'Stripe account')}
              value={shown(config.stripeAccount)}
            />
            <Field
              label={t('paymentsSetup.field.publishableKey', 'Publishable key')}
              value={maskedKey(config.publishableKey)}
            />
            {providerFields.map(([key, value]) => (
              <Field key={key} label={key} value={shown(value)} />
            ))}
          </Box>
        ) : null}
      </SectionCard>

      <ConfirmDialog
        open={confirmOpen}
        title={t('paymentsSetup.provision.title', 'Provision billing for {{name}}?', {
          name: tenant.name,
        })}
        consequence={t(
          'paymentsSetup.provision.consequence',
          "This sets up the tenant's billing on the platform's Stripe account in pay-api and activates it in {{currency}}. Charges outside the configured range are refused. It's safe to repeat.",
          { currency: tenant.currency },
        )}
        confirmLabel={t('paymentsSetup.provision.confirm', 'Provision billing')}
        busy={provisioning}
        error={provisionFailure ? provisionError(provisionFailure.error) : undefined}
        onConfirm={() => void provision()}
        onCancel={() => {
          setConfirmOpen(false);
          setProvisionFailure(null);
        }}
      />
    </Box>
  );
}
